import { useEffect, useMemo, useContext, type FC } from 'react';
import {
  FilterContext,
  PaginationContext,
  SearchContext,
  SortContext,
} from 'dash-ui';
import { useGetCurrentDeveloperPropertiesQuery } from 'gql/generated.graphql';

import type { PropertiesDataProviderProps } from './PropertiesData.interfaces';
import PropertiesDataContext from './PropertiesDataContext';

const PropertiesDataProvider: FC<PropertiesDataProviderProps> = ({
  children,
  filter = {},
}) => {
  const {
    setTotal,
    state: paginationState,
    resetPagination,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { state: sort } = useContext(SortContext);
  const { state: filterContext } = useContext(FilterContext);

  const { skip, limit } = getRefetchProps(paginationState);
  const searchByName = searchSlug.length > 2 ? searchSlug : null;

  const { statuses, organizationId } = useMemo(
    () => ({
      statuses: filterContext.statuses || [],
      organizationId: filterContext.organizationId,
    }),
    [filterContext.statuses, filterContext.organizationId]
  );

  // Adjust default sort parameters
  if (![-1, 1].includes(sort.sortOrder)) sort.sortOrder = -1;
  if (sort.fieldName === '') sort.fieldName = 'createdAt';

  const {
    data: result,
    refetch,
    loading,
  } = useGetCurrentDeveloperPropertiesQuery({
    variables: {
      sort: [sort],
      pagination: { skip, limit },
      filter: {
        statuses,
        searchByName,
        ...(organizationId && { organizationId }),
        ...filter,
      },
    },
  });
  const { data, total } = useMemo(
    () => ({
      data: result?.getCurrentDeveloperProperties?.collection || [],
      total: result?.getCurrentDeveloperProperties?.totalCount || 0,
    }),
    [result]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSlug]);

  useEffect(() => {
    refetch({
      sort: [sort],
      pagination: { skip, limit },
      filter: {
        statuses,
        organizationId,
        searchByName,
        ...filter,
      },
    });
  }, [
    filter,
    limit,
    refetch,
    searchByName,
    skip,
    sort,
    statuses,
    organizationId,
  ]);

  return (
    <PropertiesDataContext.Provider value={{ data, loading }}>
      {children}
    </PropertiesDataContext.Provider>
  );
};

export default PropertiesDataProvider;
